import clsx from "clsx";
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  SEGMENTED_PILL_ACTIVE_CLASS,
  SEGMENTED_PILL_BUTTON_CLASS,
  SEGMENTED_PILL_INACTIVE_CLASS,
  SEGMENTED_PILL_PRIMARY_FOCUS_RING_CLASS,
  SEGMENTED_PILL_TRACK_CLASS,
  SEGMENTED_UNDERLINE_ACTIVE_TEXT_CLASS,
  SEGMENTED_UNDERLINE_BUTTON_CLASS,
  SEGMENTED_UNDERLINE_DIVIDER_CLASS,
  SEGMENTED_UNDERLINE_INACTIVE_TEXT_CLASS,
  SEGMENTED_UNDERLINE_INDICATOR_CLASS,
  SEGMENTED_UNDERLINE_INDICATOR_INSET_PX,
  SEGMENTED_UNDERLINE_TRACK_CLASS,
} from "./segmentedPillTokens";

export type SegmentedPillToggleVariant = "primary" | "secondary";

export interface SegmentedPillOption<T extends string> {
  id: T;
  label: string;
  /** Optional leading icon (e.g. Calendar/List in meetings shell). */
  icon?: ReactNode;
  /** Native tooltip (e.g. extra context on hover). */
  title?: string;
  disabled?: boolean;
}

export interface SegmentedPillToggleProps<T extends string> {
  options: SegmentedPillOption<T>[];
  value: T;
  onChange: (id: T) => void;
  className?: string;
  "data-testid"?: string;
  optionTestIdPrefix?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  /** Primary = indigo pill; secondary = transparent + dividers + sliding underline. */
  variant?: SegmentedPillToggleVariant;
}

function findAdjacentEnabledIndex<T extends string>(
  options: SegmentedPillOption<T>[],
  startIndex: number,
  direction: 1 | -1,
): number | null {
  const len = options.length;
  for (let step = 1; step <= len; step += 1) {
    const index = (startIndex + direction * step + len) % len;
    if (!options[index]?.disabled) return index;
  }
  return null;
}

function findEdgeEnabledIndex<T extends string>(
  options: SegmentedPillOption<T>[],
  edge: "start" | "end",
): number | null {
  if (edge === "start") {
    const index = options.findIndex((option) => !option.disabled);
    return index === -1 ? null : index;
  }
  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (!options[index]?.disabled) return index;
  }
  return null;
}

/**
 * Pill-style segmented control for single-select filters, sorts, and mode toggles.
 * Primary: light track + indigo active pill. Secondary: transparent + dividers + underline.
 */
export function SegmentedPillToggle<T extends string>({
  options,
  value,
  onChange,
  className,
  "data-testid": testId,
  optionTestIdPrefix = "segmented-pill",
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  variant = "primary",
}: SegmentedPillToggleProps<T>) {
  const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const trackRef = useRef<HTMLDivElement>(null);
  const [underline, setUnderline] = useState({ left: 0, width: 0 });

  const activeIndex = options.findIndex((option) => option.id === value);
  const tabbableIndex =
    activeIndex >= 0 && !options[activeIndex]?.disabled
      ? activeIndex
      : findEdgeEnabledIndex(options, "start");

  const syncUnderline = useCallback(() => {
    if (variant !== "secondary" || activeIndex < 0) return;
    const button = buttonRefs.current[activeIndex];
    const track = trackRef.current;
    if (!button || !track) return;
    const inset = SEGMENTED_UNDERLINE_INDICATOR_INSET_PX;
    const width = Math.max(0, button.offsetWidth - inset * 2);
    setUnderline({ left: button.offsetLeft + inset, width });
  }, [activeIndex, variant]);

  useLayoutEffect(() => {
    syncUnderline();
  }, [syncUnderline, options.length, value]);

  useLayoutEffect(() => {
    if (variant !== "secondary") return;
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => syncUnderline());
    observer.observe(track);
    return () => observer.disconnect();
  }, [syncUnderline, variant]);

  const selectIndex = (index: number) => {
    if (index < 0) return;
    const option = options[index];
    if (!option || option.disabled) return;
    onChange(option.id);
    buttonRefs.current[index]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (options[index]?.disabled) return;

    let nextIndex: number | null = null;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        nextIndex = findAdjacentEnabledIndex(options, index, 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        nextIndex = findAdjacentEnabledIndex(options, index, -1);
        break;
      case "Home":
        event.preventDefault();
        nextIndex = findEdgeEnabledIndex(options, "start");
        break;
      case "End":
        event.preventDefault();
        nextIndex = findEdgeEnabledIndex(options, "end");
        break;
      default:
        return;
    }

    if (nextIndex == null || nextIndex === index) return;
    selectIndex(nextIndex);
  };

  const renderLabel = (label: string, icon?: ReactNode) =>
    icon ? (
      <span className="inline-flex items-center gap-1.5">
        <span className="shrink-0" aria-hidden>{icon}</span>
        {label}
      </span>
    ) : (
      label
    );

  if (variant === "secondary") {
    return (
      <div
        ref={trackRef}
        role="radiogroup"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={clsx(SEGMENTED_UNDERLINE_TRACK_CLASS, className)}
        data-testid={testId}
      >
        {options.map(({ id, label, icon, title, disabled = false }, index) => {
          const active = id === value;
          return (
            <div key={id} className="flex min-w-0 items-stretch">
              {index > 0 ? <div className={SEGMENTED_UNDERLINE_DIVIDER_CLASS} aria-hidden /> : null}
              <button
                ref={(node) => {
                  buttonRefs.current[index] = node;
                }}
                type="button"
                role="radio"
                title={title}
                aria-checked={active}
                aria-disabled={disabled || undefined}
                disabled={disabled}
                tabIndex={index === tabbableIndex ? 0 : -1}
                onClick={() => {
                  if (!disabled) onChange(id);
                }}
                onKeyDown={(event) => handleKeyDown(event, index)}
                data-testid={`${optionTestIdPrefix}-${id}`}
                className={clsx(
                  SEGMENTED_UNDERLINE_BUTTON_CLASS,
                  disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
                  active
                    ? SEGMENTED_UNDERLINE_ACTIVE_TEXT_CLASS
                    : disabled
                      ? "text-slate-500 dark:text-slate-400"
                      : SEGMENTED_UNDERLINE_INACTIVE_TEXT_CLASS,
                )}
              >
                {renderLabel(label, icon)}
              </button>
            </div>
          );
        })}
        {activeIndex >= 0 ? (
          <span
            className={SEGMENTED_UNDERLINE_INDICATOR_CLASS}
            style={{ width: underline.width, transform: `translateX(${underline.left}px)` }}
            data-testid="segmented-pill-underline-indicator"
            aria-hidden
          />
        ) : null}
      </div>
    );
  }

  const buttonClass = clsx(SEGMENTED_PILL_BUTTON_CLASS, SEGMENTED_PILL_PRIMARY_FOCUS_RING_CLASS);

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={clsx("flex", SEGMENTED_PILL_TRACK_CLASS, className)}
      data-testid={testId}
    >
      {options.map(({ id, label, icon, disabled = false }, index) => {
        const active = id === value;
        return (
          <button
            key={id}
            ref={(node) => {
              buttonRefs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            aria-disabled={disabled || undefined}
            disabled={disabled}
            tabIndex={index === tabbableIndex ? 0 : -1}
            onClick={() => {
              if (!disabled) onChange(id);
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            data-testid={`${optionTestIdPrefix}-${id}`}
            className={clsx(
              buttonClass,
              disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer",
              active
                ? SEGMENTED_PILL_ACTIVE_CLASS
                : disabled
                  ? "text-slate-500 dark:text-slate-400"
                  : SEGMENTED_PILL_INACTIVE_CLASS,
            )}
          >
            {renderLabel(label, icon)}
          </button>
        );
      })}
    </div>
  );
}
